import React, { Suspense, lazy, useEffect } from 'react'
import { Route, Switch, useLocation } from 'react-router-dom'
import Loader from 'components/Loader/Loader'

import { selectAvailableZones, selectCurrentUser, setCurrentZone } from 'store/global'
import { setDisplayedColumns } from 'store/businesses'
import { setHostsDisplayedColumns } from 'store/rentals'

import localStorageService from 'services/storage'
import { resolveZoneToRestore } from './resolveZone'
import { usePageTitle } from 'hooks/usePageTitle'

import { useDispatch, useSelector } from 'react-redux'
import { QueryParamProvider } from 'use-query-params'

import styles from './App.scss'
import AuthWrapper from 'components/AuthWrapper'
import LoginPage from 'containers/Login/Login'

const Portal = lazy(() => import(/* webpackChunkName: "portal" */ /* webpackMode: "lazy" */ './Portal'))

function App() {
  const dispatch = useDispatch()
  const currentUser: Record<string, string> = useSelector(selectCurrentUser)
  const availableZones = useSelector(selectAvailableZones)
  const location = useLocation()
  const { pathname = '', search } = location || {}

  usePageTitle()

  const restoreLastBusinessesDisplayedColumns = () => {
    const columns = localStorageService.displayedColumns
    const hostColumns = localStorageService.displayedSTRColumns

    if (columns) dispatch(setDisplayedColumns({ columns }))
    if (hostColumns) dispatch(setHostsDisplayedColumns({ columns: hostColumns }))
  }

  const onAppInitialLoad = () => {
    localStorageService.returnUrl = `${pathname}${search}`

    restoreLastBusinessesDisplayedColumns()
  }
  // TODO: make this stand in react best practice
  useEffect(onAppInitialLoad, [])

  const restoreLastSelectedZone = () => {
    const resolvedZone = resolveZoneToRestore({
      availableZones,
      currentUser,
      storedZone: localStorageService.lastSelectedZoneForUser,
    })

    if (!resolvedZone) return

    dispatch(setCurrentZone({ zone: resolvedZone }))
    localStorageService.setLastSelectedZoneForUser(currentUser, resolvedZone)
  }

  useEffect(() => {
    restoreLastSelectedZone()
    // TODO fix dependency array and the used function
  }, [currentUser, availableZones.length])

  return (
    <QueryParamProvider ReactRouterRoute={Route}>
      <div className={styles.appContainer}>
        {/*
          Single empty-currentUser loader gate lives in AuthWrapper (IVX-9755):
          it renders children only once currentUser is present, so the previous
          duplicate `isEmptyOrEmptyObject(currentUser)` check here was dead code.
          The Suspense fallback below covers the lazy Portal chunk, not auth.
        */}
        <AuthWrapper>
          <Suspense fallback={<Loader type='globalLoading' message={''} />}>
            <Portal />
          </Suspense>
        </AuthWrapper>
        <Switch>
          <Route path='/login'>
            <LoginPage />
          </Route>
        </Switch>
      </div>
    </QueryParamProvider>
  )
}

export default App
